import { Box, Button, Flex, Icon, Menu, MenuButton, MenuDivider, MenuItem, MenuList, Text, Tooltip } from "@chakra-ui/react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { FiChevronDown, FiPlus, FiTrash2 } from "react-icons/fi";
import { momentLocaleFor } from "../formatting/i18nFormatters";
import { useConfirmationModal } from "../useConfirmationModal";
import { useAssistantContext } from "./AssistantProvider";

export const ConversationSwitcher = () => {
  const { t, i18n } = useTranslation();
  const { summaries, activeConversationId, switchTo, startNew, remove, isRunning } = useAssistantContext();
  const { show, modal } = useConfirmationModal();
  const locale = momentLocaleFor(i18n.language);

  const activeTitle =
    summaries.find((conversation) => conversation.id === activeConversationId)?.title ?? t("New conversation");

  const confirmDelete = (id: string, title: string) =>
    show({
      title: t("Delete conversation"),
      description: t("Are you sure you want to delete «{{title}}»?", { title }),
      confirmButtonText: t("Delete") ?? "Delete",
      confirmButtonColor: "red",
      size: "md",
      onConfirmed: () => remove(id),
    });

  return (
    <>
      <Menu placement="bottom-end" isLazy>
        <MenuButton as={Button} variant="ghost" size="sm" rightIcon={<FiChevronDown />} isDisabled={isRunning} maxW="52">
          <Text isTruncated>{activeTitle}</Text>
        </MenuButton>
        <MenuList maxH="sm" overflowY="auto">
          <MenuItem icon={<FiPlus />} onClick={startNew} isDisabled={isRunning}>
            {t("New conversation")}
          </MenuItem>
          {summaries.length > 0 && <MenuDivider />}
          {summaries.map((conversation) => (
            <MenuItem
              key={conversation.id}
              onClick={() => void switchTo(conversation.id)}
              isDisabled={isRunning}
              bg={conversation.id === activeConversationId ? "smBackgroundSecondary" : undefined}
            >
              <Flex align="center" justify="space-between" gap="3" w="full">
                <Box minW="0">
                  <Text fontSize="sm" color="smText" isTruncated>
                    {conversation.title}
                  </Text>
                  <Text fontSize="xs" color="smTextDisabled">
                    {moment(conversation.modifiedAt).locale(locale).fromNow()}
                  </Text>
                </Box>
                <Tooltip label={t("Delete conversation")}>
                  <Box
                    as="span"
                    aria-label={t("Delete conversation") ?? "Delete conversation"}
                    color="smTextDisabled"
                    _hover={{ color: "smError" }}
                    onClick={(event) => {
                      event.stopPropagation();
                      confirmDelete(conversation.id, conversation.title);
                    }}
                  >
                    <Icon as={FiTrash2} />
                  </Box>
                </Tooltip>
              </Flex>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      {modal}
    </>
  );
};
